import { z } from 'zod';

export const MAX_SALES_ROWS = 5000;
export const MAX_SALES_BYTES = 1024 * 1024;
export type SalesGrid = string[][];
export const SalesMappingSchema = z
  .object({
    headers: z.array(z.string().max(300)).min(1).max(80),
    date: z.number().int().min(-1).max(79),
    item: z.number().int().min(-1).max(79),
    quantity: z.number().int().min(-1).max(79),
    amount: z.number().int().min(0).max(79),
    vat: z.number().int().min(-1).max(79),
    basis: z.enum(['net', 'gross']),
    decimal: z.enum(['comma', 'point']),
    dateFormat: z.enum(['dmy', 'mdy', 'iso']),
    grain: z.enum(['items', 'totals']),
    vatDefault: z.number().finite().min(0).max(100).nullable(),
    recipes: z.record(z.string().uuid()).default({}),
  })
  .strict();
export type SalesMapping = z.infer<typeof SalesMappingSchema>;
export type SalesLine = {
  source_row: number;
  date: string | null;
  item: string | null;
  quantity: number | null;
  amount: number;
  vat: number | null;
  net: number | null;
  recipe_id: string | null;
};
export type SalesImport = {
  id: string;
  tenant_id: string;
  business_id: string;
  source: string;
  filename: string;
  file_sha256: string;
  period_start: string;
  period_end: string;
  mapping: SalesMapping;
  lines: SalesLine[];
  net_total: number | null;
  amount_total: number;
  missing_net: number;
  confirmed_at?: string | null;
  archived_at?: string | null;
  void_confirmed_at?: string | null;
  void_reason?: string | null;
  version: number;
  created_at?: string;
};

export function salesDate(value: string, format: SalesMapping['dateFormat']): string | null {
  let iso = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const match = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(iso);
    if (!match || format === 'iso') return null;
    const [, a, b, year] = match;
    iso = `${year}-${(format === 'dmy' ? b : a).padStart(2, '0')}-${(format === 'dmy' ? a : b).padStart(2, '0')}`;
  }
  const date = new Date(`${iso}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === iso ? iso : null;
}

export function salesNumber(value: string, decimal: SalesMapping['decimal']): number | null {
  const input = value
    .trim()
    .replace(/\u00a0/g, ' ')
    .replace(/^(?:EUR|€)\s*|\s*(?:EUR|€)$/gi, '')
    .trim();
  const pattern =
    decimal === 'comma'
      ? /^-?(?:\d+|\d{1,3}(?:\.\d{3})+)(?:,\d{1,6})?$/
      : /^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,6})?$/;
  if (!pattern.test(input)) return null;
  const number = Number(
    decimal === 'comma' ? input.replace(/\./g, '').replace(',', '.') : input.replace(/,/g, ''),
  );
  return Number.isFinite(number) && Math.abs(number) <= 1e9 ? number : null;
}

export function parseSalesCsv(text: string, delimiter: string): SalesGrid {
  if (![',', ';', '\t'].includes(delimiter)) throw new Error('Separatore CSV non valido.');
  const rows: SalesGrid = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  let closed = false;
  const pushCell = () => {
    row.push(cell);
    cell = '';
    closed = false;
    if (row.length > 80) throw new Error('Massimo 80 colonne.');
  };
  const pushRow = () => {
    pushCell();
    rows.push(row);
    row = [];
    if (rows.length > MAX_SALES_ROWS + 100)
      throw new Error('File troppo lungo: suddividi l’estratto.');
  };
  const source = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < source.length; i++) {
    const char = source[i];
    if (quoted) {
      if (char === '"' && source[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (char === '"') {
        quoted = false;
        closed = true;
      } else cell += char;
    } else if (char === delimiter) pushCell();
    else if (char === '\n' || char === '\r') {
      pushRow();
      if (char === '\r' && source[i + 1] === '\n') i++;
    } else if (char === '"' && !cell && !closed) quoted = true;
    else {
      if (closed || char === '"') throw new Error('Virgolette CSV non valide.');
      cell += char;
    }
    if (cell.length > 2000) throw new Error('Cella troppo lunga (massimo 2.000 caratteri).');
  }
  if (quoted) throw new Error('Virgolette CSV non chiuse.');
  if (cell || row.length) pushRow();
  return rows;
}

export function suggestSalesMapping(headers: string[]): SalesMapping {
  const find = (names: string[]) => {
    const hits = headers
      .map((h, i) => (names.includes(h.trim().toLowerCase()) ? i : -1))
      .filter((i) => i >= 0);
    return hits.length === 1 ? hits[0] : -1;
  };
  return {
    headers,
    date: find(['data', 'date', 'giorno']),
    item: find(['articolo', 'prodotto', 'descrizione', 'item']),
    quantity: find(['quantità', 'quantita', 'qty', 'quantity']),
    amount: find(['totale', 'importo', 'total', 'amount', 'ricavi']),
    vat: find(['iva', 'aliquota', 'vat']),
    basis: 'gross',
    decimal: 'comma',
    dateFormat: 'dmy',
    grain: 'items',
    vatDefault: null,
    recipes: {},
  };
}

export function normalizeSales(
  grid: SalesGrid,
  headerRow: number,
  mapping: SalesMapping,
  start: string,
  end: string,
  excluded: number[] = [],
) {
  const errors: string[] = [];
  const lines: SalesLine[] = [];
  if (!SalesMappingSchema.safeParse(mapping).success)
    return { lines, errors: ['Completa la configurazione delle colonne.'] };
  if (!salesDate(start, 'iso') || !salesDate(end, 'iso') || start > end)
    return { lines, errors: ['Indica un periodo valido.'] };
  if ((Date.parse(end) - Date.parse(start)) / 86400000 > 366)
    return { lines, errors: ['Suddividi l’estratto in periodi di massimo 366 giorni.'] };
  const headers = grid[headerRow];
  if (!headers || JSON.stringify(headers) !== JSON.stringify(mapping.headers))
    return { lines, errors: ['Il formato del file è cambiato: verifica le colonne.'] };
  const selected = [
    mapping.date,
    mapping.item,
    mapping.quantity,
    mapping.amount,
    mapping.vat,
  ].filter((i) => i >= 0);
  if (new Set(selected).size !== selected.length || selected.some((i) => i >= headers.length))
    return { lines, errors: ['Ogni campo deve utilizzare una colonna distinta e presente.'] };
  if (mapping.grain === 'items' && (mapping.item < 0 || mapping.quantity < 0))
    return { lines, errors: ['Il dettaglio per articolo richiede descrizione e quantità.'] };
  for (let i = headerRow + 1; i < grid.length; i++) {
    const row = grid[i];
    if (excluded.includes(i) || row.every((c) => !c.trim())) continue;
    const fail = (message: string) => errors.push(`Riga ${i + 1}: ${message}`);
    if (row.length !== headers.length) {
      fail('numero di colonne diverso dalle intestazioni.');
      continue;
    }
    const date = mapping.date >= 0 ? salesDate(row[mapping.date], mapping.dateFormat) : null;
    if (mapping.date >= 0 && (!date || date < start || date > end)) {
      fail('data assente, non valida o fuori periodo.');
      continue;
    }
    const amount = salesNumber(row[mapping.amount], mapping.decimal);
    const quantity =
      mapping.grain === 'items' ? salesNumber(row[mapping.quantity], mapping.decimal) : null;
    const item = mapping.grain === 'items' ? row[mapping.item].trim() : null;
    const vat =
      mapping.vat >= 0
        ? salesNumber(row[mapping.vat].replace(/%$/, ''), mapping.decimal)
        : mapping.vatDefault;
    if (amount === null || Math.abs(amount * 100 - Math.round(amount * 100)) > 0.00001) {
      fail('importo non valido: servono totali di riga in euro, massimo due decimali.');
      continue;
    }
    if (
      mapping.grain === 'items' &&
      (quantity === null ||
        !item ||
        item.length > 300 ||
        (quantity === 0 && amount !== 0) ||
        (amount !== 0 && Math.sign(amount) !== Math.sign(quantity!)))
    ) {
      fail('descrizione, quantità o segno del reso non coerenti.');
      continue;
    }
    if ((mapping.vat >= 0 && vat === null) || (vat !== null && (vat < 0 || vat > 100))) {
      fail('aliquota IVA non valida (esempio: 10, non 0,10).');
      continue;
    }
    const net =
      mapping.basis === 'net'
        ? amount
        : vat === null
          ? null
          : Math.round((amount / (1 + vat / 100) + Math.sign(amount) * Number.EPSILON) * 100) / 100;
    lines.push({
      source_row: i + 1,
      date,
      item,
      quantity,
      amount,
      vat,
      net,
      recipe_id: item ? (mapping.recipes[item] ?? null) : null,
    });
  }
  if (!lines.length && !errors.length) errors.push('Nessuna riga da importare.');
  if (lines.length > MAX_SALES_ROWS) errors.push('Massimo 5.000 righe per importazione.');
  return { lines, errors };
}

export function summarizeSales(lines: SalesLine[]) {
  const cents = (n: number) => Math.round(n * 100);
  const missing = lines.filter((line) => line.net === null).length;
  return {
    amount: lines.reduce((sum, line) => sum + cents(line.amount), 0) / 100,
    net: missing ? null : lines.reduce((sum, line) => sum + cents(line.net!), 0) / 100,
    missing,
    linked: lines.filter((line) => line.recipe_id).length,
  };
}

export function salesImportConflict(
  imports: SalesImport[],
  business: string,
  source: string,
  sha: string,
  start: string,
  end: string,
): string | null {
  const active = imports.filter((batch) => batch.business_id === business && !batch.archived_at);
  if (active.some((batch) => batch.file_sha256 === sha))
    return 'Questo file è già presente per il locale.';
  if (
    active.some(
      (batch) =>
        batch.source.trim().toLowerCase() === source.trim().toLowerCase() &&
        batch.period_start <= end &&
        batch.period_end >= start,
    )
  )
    return 'Il periodo si sovrappone a un’importazione della stessa fonte. Annulla prima quella da sostituire.';
  return null;
}
